/**
 * Created by Ella Ma on 01/04/2019.
 * Description:
 *
 * ------ maintenance history ------
 * 05/21/2019 Bowen Li - switch the icon order: Dashboard - UI issues when view dashboard - meatball
 * 05/21/2019 Bowen Li - hide the edit button on ipad
 * 08/27/2019 Ella  Ma - grid multi or tiered sorting
 * 08/13/2019 Yoyo     - column chart refactored as plugined service based
 * 09/05/2019 Yoyo     - component config init logic move to child level
 * 11/10/2019 Yoyo     - line chart refactored as plugined service based
 * 11/10/2019 Yoyo     - metadata tile refactored as plugined service based
 * 11/11/2019 Yoyo     - value tile refactored as plugined service based
 * 11/14/2019 Yoyo     - simplify "_executeQuery“ from rxjs pipe style to plain "_reloadWidget"
 */
import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, catchError, take, switchMap, filter, delay } from 'rxjs/operators';
import { DeviceDetectorService } from 'ngx-device-detector';

import { AppState, smartSelector } from '../../../redux';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { TamDashboardOptions, TamDashboardTileType, TamDashboardActions, TamDashboardEvents, TileIcons, FocusInfo } from '../../../components/tam-dashboard/tam-dashboard.model';
import { SavedDashboard, ScreenFocusType } from '../../../models/saved-screen';
import { ScreenActionTypes } from '../../../redux/actions/screen.actions';
import { forkJoin, Subscription, of, Subject } from 'rxjs';
import { QueryService } from '../../../services/query.service';
import { TileService } from '../../../tamalelibs/services/tile.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { GridType } from 'angular-gridster2';
import { DashboardService } from '../../../pages/dashboard/dashboard.service';
import { DashboardScreenInitializeFocus } from '../../../redux/actions/dashboard-screen.actions';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { getFocusStateSelector } from '../../../redux/reducers/dashboard.reducer';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { EntityService } from '../../../tamalelibs/services/entity.service';
import { HomeScreenService } from '../../../services/home-screen.service';
import { businessConstants } from '../../../constants/business.constants';
import { TamDashboardColumnChartTileConfiguration } from '../tam-dashboard-column-chart-tile/tam-dashboard-column-chart-tile.config';
import { TamDashboardGridTileConfiguration } from '../tam-dashboard-grid-tile/tam-dashboard-grid-tile.config';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { LayoutService } from '../../../services/layout.service';
import { TamDashboardLineChartTileConfiguration } from '../tam-dashboard-line-chart-tile/tam-dashboard-line-chart-tile.config';
import { TamDashboardMetadataTileConfiguration } from '../tam-dashboard-metadata-tile/tam-dashboard-metadata-tile.config';
import { TamDashboardValueTileConfiguration } from '../tam-dashboard-value-tile/tam-dashboard-value-tile.config';
import { TamDashboardPieChartTileConfiguration } from '../tam-dashboard-pie-chart-tile/tam-dashboard-pie-chart-tile.config';
import { TamDashboardPopUpService } from '../tam-dashboard-popup.service';
import { TamDashboardThreadTileConfiguration } from '../tam-dashboard-thread-tile/tam-dashboard-thread-tile.config';
import { TamDashboardRichTextTileConfiguration } from '../tam-dashboard-rich-text-tile/tam-dashboard-rich-text-tile.config';
import { getFocusState } from '../../../redux/reducers/blastemail-view.reducer';
import { Base64Service } from '../../../tamalelibs/services/base64.service';
import { DashboardTileService } from '../dashboard-tile.service';
import { TamDashboardMapTileConfiguration } from '../tam-dashboard-map-tile/tam-dashboard-map-tile.config';
import { ContactViewModel } from '../../../tamalelibs/components/contact.view-model';
import { EntityViewModelForWeb } from '../../entity-dialog/entity-view-web.model';
import { ContactService } from '../../../tamalelibs/services/contact.service';
import { DashboardEditorSubChromeActionType, DashboardEditorSubChromeConfig, DashboardEditorSubChromeEventType } from './dashboard-editor-sub-chrome/dashboard-editor-sub-chrome.model';
import { Entity } from '../../../tamalelibs/models/entity.model';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';

@Component({
    selector: 'dashboard-screen',
    templateUrl: './dashboard-viewer.component.html',
    styleUrls: ['./dashboard-viewer.component.scss']
})
export class DashboardViewerComponent implements OnInit, OnDestroy {

    @Input() screenKey: string; // load dashboard
    @Input() storeKey?: string; // get focus from store
    @Input() screenType?: string; // from homescreen or navigation menu
    @Input() loadingSpecificScreen?; // don't load when refreshing homescreen
    @Input() firstPageTabId?: string; // the first dashboard tab should be loaded when refreshing homescreen
    @Input() pageType?: string; // which page call dashbaord, home screen, entity screen or contact screen
    @Input() embedInRichClient?: boolean;
    @Input() actionSubject$?: Subject<any>; // event should be triger when change dashboard tab or add dashboard tab

    // rich client dashboard input
    @ViewChild('finder', { static: true }) finderEle: any;

    dashboardOptions: TamDashboardOptions = new TamDashboardOptions(true);
    dashboardViewSubChromeConfig: DashboardEditorSubChromeConfig = new DashboardEditorSubChromeConfig();
    isPageLoading: boolean;
    focusedEntity: any;

    private _destroySubscriptions: Array<Subscription> = [];
    private _screenData: SavedDashboard;
    private _screenInfo: any;
    private _source = {};
    private _isIPAD = false;
    private _tileConfigurationMap = new Map();

    constructor(
        private _deviceService: DeviceDetectorService,
        private _dashboardService: DashboardService,
        private _dashboardTileService: DashboardTileService,
        private _entityService: EntityService,
        private _layoutService: LayoutService,
        private _queryService: QueryService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _screenService: ScreenService,
        private _store: Store<AppState>,
        private _tileService: TileService,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _homeScreenService: HomeScreenService,
        private _utils: UtilsService,
        private _dashboardPopUpService: TamDashboardPopUpService,
        private _base64: Base64Service,
        private _contactViewModel?: ContactViewModel,
        private _entityViewModel?: EntityViewModelForWeb,
        private _contactService?: ContactService,
    ) {
        this._isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        // don't init components when loading homescreen
        if (!this.loadingSpecificScreen && this.firstPageTabId === this.storeKey) {
            this.initComponent(this.screenKey);
        }
        if (this.actionSubject$) {
            // tigger init components when adding/changing dashboard tab on homescreen, rich-client dashboard
            this._destroySubscriptions.push(
                this.actionSubject$.subscribe(event => this._onSubscribeActionsEvent(event))
            );
        }
        this._destroySubscriptions.push(
            this.dashboardOptions.feedbackSubject$.subscribe(event => this._onSubscribeEvent(event)),
            this.dashboardViewSubChromeConfig.actions$.subscribe(event => this._onSunChromeSubscribeActionsEvent(event))
        );

        if (this.embedInRichClient) {
            this._destroySubscriptions.push(
                this.actionSubject$.pipe(
                    filter(item => item.type === 'CHANGE_FOCUS')
                ).subscribe((data) => {
                    if (this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                        this._setValueForFocus(data.payload).subscribe(innerData => {
                            if (innerData) {
                                this.onFinderValueChange([innerData]);
                            }
                        });
                    }
                })
            );
        }

        const currentUser = this._storeQuerier.getCurrentUser();
        // add the refresh & edit icon
        this.dashboardOptions.icons = [];

        // rich client dashboard only has refersh icon
        // if the device is ipad, the edit button will hide
        if (currentUser && currentUser.credential &&
            currentUser.credential.publishPubConfiguration && !this._isIPAD && !this.embedInRichClient) {
            if (this.screenKey !== businessConstants.activityScreenId) {
                this.dashboardOptions.icons.push(TileIcons.EDIT);
            }
        }
        this.dashboardOptions.icons.push(TileIcons.REFRESH);
        // this.dashboardOptions.icons.push(TileIcons.HIDE_BORDER);
        this.dashboardOptions.icons.push(TileIcons.HIDE_TITLE);

        this.dashboardViewSubChromeConfig.storeKey = this.storeKey;
        this.dashboardViewSubChromeConfig.fromPage = this.pageType;
        this.dashboardViewSubChromeConfig.actions$.pipe(
            filter(item => item.type === DashboardEditorSubChromeActionType.onFinderValueChange)
        ).subscribe(data => {
            this.onFinderValueChange(data.payload);
        });

        // rich client dashboard disabled right click grid tile
        this._dashboardService.embedInRichClient = this.embedInRichClient;
    }

    ngOnDestroy() {
        this._clearFocusValue();
        this._tileConfigurationMap.clear();
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    initComponent(screenKey: string) {
        const screenId = screenKey;
        this.isPageLoading = true;
        this._screenService.getById(screenId).pipe(
            take(1),
            switchMap((screen: any) => {
                // TODO: Changed a way to get screen info
                if (this.screenType !== businessConstants.screenType.dashboard) {
                    // set page title
                    this._screenInfo = {
                        url: this._route.snapshot.url,
                        name: screen.name,
                        type: 'DASHBOARD',
                        id: screenId
                    };
                    this._store.dispatch({
                        type: ScreenActionTypes.SET_VALUE,
                        payload: this._screenInfo
                    });
                    this.dashboardViewSubChromeConfig.events$.next({
                        type: DashboardEditorSubChromeEventType.changeInfo,
                        payload: this._screenInfo
                    });
                }

                if (typeof screen.configuration.focus.type === 'string') {
                    screen.configuration.focus.type = parseInt(screen.configuration.focus.type, 10);
                }

                if (!screen.configuration.widgets) {
                    // clear print store data
                    this._clearPrintStoreData();
                    // home screen loading
                    this._homeScreenService.reloadingAssignedTab$.next({ type: businessConstants.screenType.dashboard });
                    return of(null);
                }
                screen.configuration.widgets = screen.configuration.widgets.filter(item => item.tileId);
                if (!screen.configuration.widgets || screen.configuration.widgets.length === 0) {
                    this.isPageLoading = false;
                    this._screenData = screen;
                    this.dashboardViewSubChromeConfig.events$.next({
                        type: DashboardEditorSubChromeEventType.changeData, payload: screen
                    });
                    // clear print store data
                    this._clearPrintStoreData();
                    // home screen loading
                    this._homeScreenService.reloadingAssignedTab$.next({ type: businessConstants.screenType.dashboard });
                    return of(null);
                } else {
                    // load tile configuration from server
                    const observables = [];
                    screen.configuration.widgets.forEach(element => {
                        observables.push(this._tileService.getById(element.tileId));
                    });
                    return forkJoin(observables).pipe(
                        map(tiles => {
                            const length = screen.configuration.widgets.length;
                            for (let i = 0; i < length; i++) {
                                screen.configuration.widgets[i].tile = tiles[i];
                            }
                            return screen;
                        })
                    );
                }
            }),
            filter(screen => screen), // handle the case that the dashboard has no tile
            map(screen => {
                // init tiles without data
                this._screenData = screen;
                this.dashboardViewSubChromeConfig.screenData = screen;
                if (screen.configuration.focus.entityTypes && screen.configuration.focus.entityTypes.length > 0) {
                    this._source['entityTypes'] = screen.configuration.focus.entityTypes;
                }
                this.dashboardViewSubChromeConfig.events$.next({
                    type: DashboardEditorSubChromeEventType.changeData,
                    payload: screen
                });
                const gridType = GridType.VerticalFixed;
                return this._dashboardService.formatDashboardScreen(screen, this.dashboardOptions, gridType, true);
            }),
            delay(0), // use delay to make sure every tile was initialized
            switchMap((screen) => {
                const observables = [];
                if (this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                    if (this.screenType) {
                        const focusInfo = this._storeQuerier.queryBySelector(this._relativeSelector(this.storeKey, getFocusState));
                        if (focusInfo && focusInfo.entity) {
                            if (Array.isArray(focusInfo.entity)) {
                                observables.push(this._setValueForFocus(focusInfo.entity[0]));
                            } else {
                                observables.push(this._setValueForFocus(focusInfo.entity));
                            }
                        }
                    } else {
                        const storeFocusValue = this._storeQuerier.queryBySelector(getFocusStateSelector);
                        if (storeFocusValue && storeFocusValue.shortName) {
                            observables.push(this._setValueForFocus(storeFocusValue));
                        }
                    }
                }
                if (observables.length === 0) {
                    return of({ screen, focusedEntity: null });
                } else {
                    return forkJoin(observables).pipe(
                        map((focusedEntity) => ({ screen, focusedEntity })),
                    );
                }
            }),
            switchMap(({ screen, focusedEntity }) => {
                if (focusedEntity && focusedEntity.length > 0) {
                    this.focusedEntity = focusedEntity[0];
                }
                this.isPageLoading = false;
                const observables = [];
                screen.configuration.widgets.forEach(element => {
                    if (element.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
                        screen.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                        if (focusedEntity && focusedEntity.length > 0) {
                            this.dashboardViewSubChromeConfig.events$.next({
                                type: DashboardEditorSubChromeEventType.setFoucsValue,
                                payload: focusedEntity[0]
                            });
                            this._queryService.prepareRelationshipFocusForQuery(focusedEntity[0].id,
                                element.tile.focus.relationshipTypes)
                                .pipe(take(1))
                                .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds, true));
                        } else {
                            this._initWidget(element);
                            this._setTileNoData(element);
                            // print no data should save tilesetting store for hide/show tile header
                            this._saveTileConfigStore(element.id, element.tile);
                        }
                    } else {
                        this._toggleLoadingStatus(element.id, true);
                        this._queryService.prepareFocusForQuery(element.tile, screen.configuration.focus)
                            .pipe(take(1))
                            .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds, true));
                    }
                });
                // home screen loading
                this._homeScreenService.reloadingAssignedTab$.next({ type: businessConstants.screenType.dashboard });
                // for dashboard wrapper page set loading is false
                if (this.actionSubject$) {
                    this.actionSubject$.next({
                        type: 'SET_PAGE_LOADING_STATUE'
                    });
                }
                return forkJoin(observables);
            }),
            catchError((e: any) => {
                this._router.navigate(['error']);
                return of(null);
            })
        ).subscribe();
    }

    onFinderValueChange(value: Array<EntityBrief>) {
        this.focusedEntity = value;
        // save the finder entity to store for homescreen
        const focusInfo = new FocusInfo();
        focusInfo.entity = value;
        this._store.dispatch(new DashboardScreenInitializeFocus(this.storeKey, focusInfo));
        if (this._screenData) {
            this._screenData.configuration.widgets
                .filter(element => element.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL)
                .forEach(element => {
                    if (!value || value.length === 0) {
                        this._setTileNoData(element);
                        return;
                    }
                    this._toggleLoadingStatus(element.id, true);
                    // There's no case in which the one-degree is enabled and tiles are added relationship type filters
                    // So ignore the relationship type config for any tile when the dashboard has been enabled one-degree
                    // The length of value is one means that the one degree is disabled on current dashboard.
                    this._queryService.prepareRelationshipFocusForQuery(value.length === 1 ? value[0].id : value.map(ve => ve.id),
                        element.tile.focus.relationshipTypes)
                        .pipe(take(1))
                        .subscribe((focusedEntityIds) => this._reloadWidget(element, focusedEntityIds, true));
                });
        }
    }

    private _clearFocusValue() {
        if (this.dashboardViewSubChromeConfig.clearStoreFocusValue) {
            this._store.dispatch({
                type: DashboardActionTypes.SET_FOCUS_VALUE,
                payload: null
            });
        }
    }

    private _editTile(tileId) {
        this.dashboardViewSubChromeConfig.events$.next({
            type: DashboardEditorSubChromeEventType.editTile,
            payload: {
                screenKey: this.screenKey,
                tileId: tileId
            }
        });
    }

    private _resetPrintStoreData(id, data) {
        this._store.dispatch({
            type: DashboardActionTypes.RESET_TILE_DATA,
            payload: {
                id, data
            }
        });
    }

    private _clearPrintStoreData() {
        this._store.dispatch({
            type: DashboardActionTypes.CLEAR_PRINT_TILE,
        });
    }

    private _relativeSelector(key, selector) {
        return (state) => {
            return selector(smartSelector(key, 'dashboardScreenReducer')(state));
        };
    }

    private _getTileConfigurationByType(type: TamDashboardTileType): any {
        switch (type) {
            case TamDashboardTileType.PIE_CHART:
                return new TamDashboardPieChartTileConfiguration(this._dashboardTileService, this._dashboardService, this._store);
            case TamDashboardTileType.COLUMN_CHART:
            case TamDashboardTileType.BAR_CHART:
                return new TamDashboardColumnChartTileConfiguration(this._dashboardTileService, this._dashboardService,
                    this._store, this._dashboardPopUpService, type === TamDashboardTileType.BAR_CHART);
            case TamDashboardTileType.LINE_CHART:
                return new TamDashboardLineChartTileConfiguration(this._dashboardTileService, this._dashboardService, this._store);
            case TamDashboardTileType.VALUE:
                return new TamDashboardValueTileConfiguration(this._dashboardTileService, this._store);
            case TamDashboardTileType.METADATA:
                return new TamDashboardMetadataTileConfiguration(this._dashboardTileService, this._store);
            case TamDashboardTileType.NOTES:
                const config = new TamDashboardThreadTileConfiguration(this._store, this._dashboardTileService);
                config.clickable = true;
                return config;
            case TamDashboardTileType.EDITOR:
                return new TamDashboardRichTextTileConfiguration(this._base64, this._store, this._dashboardTileService);
            case TamDashboardTileType.MAP:
                return new TamDashboardMapTileConfiguration(this._store, this._dashboardTileService, this._contactViewModel, this._entityService, this._entityViewModel, this._contactService);
            case TamDashboardTileType.GRID:
            default:
                return new TamDashboardGridTileConfiguration(this._dashboardService,
                    this._deviceService, this._utils, this._dashboardTileService, this._layoutService, this._store, this._storeQuerier);
        }
    }

    private _setTileEntityIds(widget, focusedEntityIds) {
        let ids = focusedEntityIds;
        if (widget.tile.focus.type === ScreenFocusType.ALL) {
            ids = 'all';
        } else if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this._screenData.configuration.focus.type === ScreenFocusType.ALL) {
            ids = 'all';
        }
        this._dashboardService.setTileEntityIds({
            id: widget.id,
            focusedEntityIds: ids
        });
    }

    private _reloadWidget(widget: any, focusedEntityIds, initTile = false, notGetLayoutFromServer = false) {
        try {
            // per tile type, initialize tile with configuration
            if (initTile) {
                this._initWidget(widget);
            }
            if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL && this._screenData.configuration.focus.includeSpecificEntityItself) {
                focusedEntityIds = focusedEntityIds + ',' + this._screenData.configuration.focus.entityId;
            } else if (widget.tile.focus.includeSpecificEntityItself) {
                focusedEntityIds = focusedEntityIds + ',' + widget.tile.focus.entityId;
            }
            if (focusedEntityIds === '') {
                this._setTileNoData(widget);
                return;
            }

            this._setTileEntityIds(widget, focusedEntityIds);
            // reload the tile
            // for grid wait for ready, initialization includes reloading layout
            // so if it is not inited, reload layout first
            if (widget.tile.visualization.type === TamDashboardTileType.GRID) {
                if (initTile) {
                    this.dashboardOptions.feedbackSubject$.pipe(
                        filter((event) => event.id === widget.id && event.type === TamDashboardEvents.SET_CONFIG_FINISHED),
                        take(1)
                    ).subscribe(() => {
                        this._setFocusedEntityIds(widget.id, focusedEntityIds);
                        this._loadData(widget.id, widget.tile, focusedEntityIds);
                    });
                } else {
                    // click 'Refresh' button, grid not get layout from server, keep current layout.
                    if (!notGetLayoutFromServer) {
                        this._loadGridLayout(widget);
                    }
                    this._setFocusedEntityIds(widget.id, focusedEntityIds);
                    this._loadData(widget.id, widget.tile, focusedEntityIds);
                }
            } else {
                this._setFocusedEntityIds(widget.id, focusedEntityIds);
                this._loadData(widget.id, widget.tile, focusedEntityIds);
            }

            // label related
            if (widget.tile.visualization.type === TamDashboardTileType.PIE_CHART) {
                this._setPieChartLabelDispaly(widget.id, widget.tile.visualization.settings.displaySetting);
            }
            if (widget.tile.visualization.type === TamDashboardTileType.LINE_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.COLUMN_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.BAR_CHART ||
                widget.tile.visualization.type === TamDashboardTileType.PIE_CHART) {
                const series = widget.tile.visualization.settings.series;
                const column = widget.tile.columns.filter(item => item.guid === series);
                if (column && column.length > 0) {
                    const dataType = column[0].dataType;
                    if (['number', 'currency', 'percent', 'ratio'].indexOf(dataType) !== -1) {
                        this._setChartLabelSetting(widget.id, {
                            dataType: dataType,
                            settings: column[0].settings
                        });
                    }
                }
            }

            this._setTileConfig(widget.id, widget.tile);
        } catch (e) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: e.message,
            });
            this._setTileNoData(widget);
            this._toggleLoadingStatus(widget.id, false);
        }
    }

    private _onSubscribeEvent(event) {
        if (event.type === TamDashboardEvents.ITEM_ICON_CLICK) {
            if (event.payload.icon === TileIcons.REFRESH.icon) {
                const widget = this._screenData.configuration.widgets.filter(element => element.id === event.payload.id)[0];
                if (widget && widget.tile.visualization.type === TamDashboardTileType.GRID) {
                    this._refreshSingleGridTile(event.payload.id);
                } else {
                    this._refreshSingleTile(event.payload.id);
                }
            } else if (event.payload.icon === TileIcons.EDIT.icon) {
                this._editTile(event.payload.id);
            }
        }
    }

    private _onSubscribeActionsEvent(event) {
        // tigger init components when adding/changing dashboard tab on homescreen
        if (event.type === 'REFRESH_DASHBOARD') {
            if (this.storeKey === event.payload.storeKey) {
                const screenKey = event.payload.id;
                this.initComponent(screenKey);
            }
        } else if (event.type === 'CHANGE_FOCUS') {// rich-client dashboard
            if (this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
                this._setValueForFocus(event.payload).subscribe(data => {
                    if (data) {
                        this.onFinderValueChange([data]);
                    }
                });
            }
        }
    }

    private _onSunChromeSubscribeActionsEvent(event) {
        if (event.type === DashboardEditorSubChromeActionType.saveGridLayout) {
            if (this._screenData && this._screenData.configuration && this._screenData.configuration.widgets) {
                this._screenData.configuration.widgets.forEach((widget) => {
                    if (widget.tile.visualization.type === TamDashboardTileType.GRID) {
                        this._dashboardService.getGridLayoutService(widget.id, this.dashboardOptions.actionSubject$).pipe(take(1))
                            .subscribe((data) => {
                                // get current grid layout for print, not default layout
                                this._store.dispatch({
                                    type: DashboardActionTypes.SAVE_GRID_TILE_LAYOUT,
                                    payload: {
                                        id: widget.id,
                                        layout: data.payload.layout,
                                    }
                                });
                            });
                    }
                });
            }
        }
    }

    private _refreshSingleGridTile(tileId) {
        this._dashboardService.getGridLayoutService(tileId, this.dashboardOptions.actionSubject$).pipe(take(1))
            .subscribe((event) => {
                const widget = this._screenData.configuration.widgets.filter(element => element.id === tileId)[0];
                if (widget && widget.tile) {
                    widget.tile.layout = event.payload.layout;
                }
                if (widget) {
                    // sync=>async
                    this._setGridLayout(widget);
                }
                this._refreshSingleTile(tileId, true);
            });
    }

    // refresh tile
    private _refreshSingleTile(id, notGetLayoutFromServer = false) {
        const widget = this._screenData.configuration.widgets.filter(element => element.id === id)[0];

        if (!widget) {
            return;
        }

        if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this.focusedEntity == null) {
            return;
        }

        this._toggleLoadingStatus(widget.id, true);
        let focusedEntity$;
        if (widget.tile.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL &&
            this._screenData.configuration.focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
            const focusIDs = Array.isArray(this.focusedEntity) ? Array.from(this.focusedEntity).map(fe => fe.id) : this.focusedEntity.id;
            focusedEntity$ = this._queryService.prepareRelationshipFocusForQuery(focusIDs,
                widget.tile.focus.relationshipTypes);
        } else {
            focusedEntity$ = this._queryService.prepareFocusForQuery(widget.tile, this._screenData.configuration.focus);
        }
        focusedEntity$.pipe(take(1)).subscribe((focusedEntityIds) => this._reloadWidget(widget, focusedEntityIds, false, notGetLayoutFromServer));
    }

    private _setPieChartLabelDispaly(id: string, displaySetting: string) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_PIR_CHART_DISPLAY,
            payload: displaySetting,
        });
    }

    private _setChartLabelSetting(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_CHART_LABEL_SETTING,
            payload: data,
        });
    }

    private _setTileData(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_DATA,
            payload: data,
        });
        // reset print store data
        this._resetPrintStoreData(id, data);
    }

    private _initTile(id: string, data: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.INIT_TILE,
            payload: data,
        });
    }

    private _loadData(id: string, tileConfig: any, focusedEntityIds: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.LOAD_DATA,
            payload: {
                tileConfig: tileConfig,
                focusedEntityIds: focusedEntityIds,
                saveStore: true
            },
        });
    }

    private _setFocusedEntityIds(id: string, focusedEntityIds: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_FOCUSED_ENTITY_IDS,
            payload: focusedEntityIds
        });
    }

    private _setTileConfig(id: string, tileConfig: any) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_TILE_CONFIG,
            payload: tileConfig
        });
        this._saveTileConfigStore(id, tileConfig);
    }

    private _saveTileConfigStore(id: string, tileConfig: any) {
        this._store.dispatch({
            type: DashboardActionTypes.SAVE_TILE_CONFIG,
            payload: {
                id,
                config: tileConfig
            }
        });
    }

    private _toggleLoadingStatus(id: string, isLoading: boolean) {
        this.dashboardOptions.actionSubject$.next({
            id: id,
            type: TamDashboardActions.SET_LOADING_STATUS,
            payload: isLoading
        });
    }

    private _setValueForFocus(focusInfo) {
        return this._entityService.getEntityListBySearchTextQuick(1, 10, focusInfo.shortName, this._source).pipe(
            map(response => this._entityService.mapEntityBriefList(response)),
            take(1),
            map((filteredEntityList) => {
                const index = filteredEntityList.findIndex(element => element.id === focusInfo.id);
                if (index !== -1) {
                    return focusInfo;
                }
                return null;
            })
        );
    }

    private _setGridLayout(widget) {
        this.dashboardOptions.actionSubject$.next({
            id: widget.id,
            type: TamDashboardActions.SET_GRID_LAYOUT,
            payload: widget
        });
    }

    private _loadGridLayout(widget) {
        this.dashboardOptions.actionSubject$.next({
            id: widget.id,
            type: TamDashboardActions.LOAD_GRID_LAYOUT,
            payload: {
                widget
            }
        });
    }

    private _initWidget(widget) {
        // confirm every tile config has subscribe once
        if (this._tileConfigurationMap.has(widget.id)) {
            const tileConfiguration = this._tileConfigurationMap.get(widget.id);
            if (tileConfiguration.subscriptions && tileConfiguration.subscriptions.length > 0) {
                tileConfiguration.subscriptions.forEach((item) => {
                    item.unsubscribe();
                });
            }
        }
        const config = this._getTileConfigurationByType(widget.tile.visualization.type);
        this._tileConfigurationMap.set(widget.id, config);
        this._initTile(widget.id, { config: config, widget: widget });
    }

    private _setTileNoData(element) {
        // except textarea tile which is not affected by focus entity, other tiles show no data msg to user
        if (element.tile.visualization.type !== TamDashboardTileType.EDITOR) {
            this._setTileData(element.id, null);
        }
    }
}
